/**
 * Shared URL search-param enum reader/writer (ADR-0003).
 * Cross-route search merge uses a loose patch; Zod revalidates on read.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { z } from 'zod';

export function useSearchEnumParam<T extends string>(
  key: string,
  schema: z.ZodType<T>,
  fallback: T,
): {
  readonly value: T;
  readonly setValue: (next: T) => void;
} {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const raw = (search as Record<string, unknown>)[key];
  const parsed = schema.safeParse(raw);
  const value: T = parsed.success ? parsed.data : fallback;

  const setValue = useCallback(
    (next: T) => {
      void navigate({
        // @ts-expect-error — cross-route search patch; Zod revalidates on read
        search: (prev: Record<string, unknown>) => ({ ...prev, [key]: next }),
        replace: true,
      });
    },
    [key, navigate],
  );

  return { value, setValue };
}
